import React, { useState } from "react";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Button,  ThemeProviderV2} from '@blueprint/ui';
import { useModal } from "react-modal-hook";
import styled from "styled-components";

import usersApi from "../../../api/users";
import { Loader, toast } from "../../../common/components/core";
import { InfoIcon } from "../../../common/components/core/icons";
import { useAuthErrorHandlerCreator } from "../../../common/hooks";
import { useGeneralErrorHandlerCreator } from "../../../common/hooks/useGeneralErrorHandler";
import {
    GenericError,
    composeErrorHandlers,
} from "../../../utils/errorHandling";

export enum UpdateEmailNotificationAction {
    ENABLE,
    DISABLE,
}

type UpdateEmailNotificationHandler = {
    isSubmitting: boolean;
    handler: (
        action: UpdateEmailNotificationAction,
        name: string,
        userId: string,
    ) => void;
};

const useUpdateEmailNotificationHandler = (
    callBackOnSuccess: () => void,
): UpdateEmailNotificationHandler => {
    const [isSubmitting, setSubmitting] = useState(false);
    const [action, setAction] = useState<UpdateEmailNotificationAction>();
    const [name, setName] = useState<string>();
    const [userId, setUserId] = useState<string>("");
    const onAuthError = useAuthErrorHandlerCreator();
    const useGeneralError = useGeneralErrorHandlerCreator();

    const [show, hide] = useModal(() => {
        const confirm = async () => {
            setSubmitting(true);
            try {
                action === UpdateEmailNotificationAction.ENABLE
                    ? await usersApi.enableHsbcOperatorEmailNoti(userId)
                    : await usersApi.disableHsbcOperatorEmailNoti(userId);
                callBackOnSuccess && callBackOnSuccess();
                toast(
                    isEnable
                        ? "Email Notifications Enabled"
                        : "Email Notifications Disabled",
                );
            } catch (e) {
                composeErrorHandlers(
                    onAuthError,
                    useGeneralError,
                )(e as GenericError);
            }
            setSubmitting(false);
            hide();
        };

        const isEnable = action === UpdateEmailNotificationAction.ENABLE;

        return (
            <ThemeProviderV2>
            <Modal show onHide={hide} backdrop="static" centered>
                <Loader overlay isLoading={isSubmitting}>
                    <Modal.Header className="justify-content-start">
                        <h2 className="mb-0">
                            {isEnable
                                ? "Activate Email Notifications"
                                : "Disable Email Notifications"}
                        </h2>
                        <OverlayTrigger
                            placement="right-start"
                            overlay={
                                <StyledToolTip id="updateEmailNotificationToolTip">
                                    <p className="m-0 fs-7 text-white">
                                        Disabling the email notifications for
                                        the HSBC Operators will mean that they
                                        will no longer receive email
                                        notifications relating to any new
                                        invoices submitted to the bank.
                                    </p>
                                </StyledToolTip>
                            }
                        >
                            <InfoIcon className="mx-2" />
                        </OverlayTrigger>
                        {/* <OverlayTrigger
                            placement="right-start"
                            overlay={({
                                placement: _placement,
                                arrowProps: _arrowProps,
                                show: _show,
                                popper: _popper,
                                hasDoneInitialMeasure: _hasDoneInitialMeasure,
                                ...props
                            }) => (
                                <div
                                    {...props}
                                    style={{
                                        position: "absolute",
                                        zIndex: 10,
                                        backgroundColor:
                                            "rgba(163, 163, 163, 0.8)",
                                        padding: "12px",
                                        color: "#FFFFFF",
                                        borderRadius: 4,
                                        ...props.style,
                                    }}
                                >
                                    Simple tooltip
                                </div>
                            )}
                        >
                            <InfoIcon />
                        </OverlayTrigger> */}
                    </Modal.Header>
                    <Modal.Body>
                        <p>{`Are you sure you want to ${
                            isEnable ? "enabled" : "disable"
                        } email notifications for the HSBC Operator  “${name}”?`}</p>
                    </Modal.Body>

                    <Modal.Footer className="d-flex justify-content-between px-3">
                        <Button variant="light.tertiary" onClick={hide}>
                            Cancel
                        </Button>
                        <Button variant="light.primary" onClick={confirm}>
                            Confirm
                        </Button>
                    </Modal.Footer>
                </Loader>
            </Modal>
            </ThemeProviderV2>
        );
    }, [action, name, isSubmitting, onAuthError]);

    return {
        isSubmitting,
        handler: (action, name, userId) => {
            setAction(action);
            setName(name);
            setUserId(userId);
            show();
        },
    };
};

export default useUpdateEmailNotificationHandler;

const StyledToolTip = styled(Tooltip)`
    .tooltip-arrow {
        display: none;
    }
    .tooltip-inner {
        background-color: rgba(163, 163, 163, 0.8);
        max-width: 360px;
        padding: 12px;
        text-align: left;
    }
`;
